// frontend/src/features/tasks/TaskInstanceCard.tsx

import {
  Text,
  Group,
  Badge,
  ActionIcon,
  Menu,
  Tooltip,
  Box,
} from '@mantine/core';
import {
  IconCircle,
  IconClock,
  IconPlayerSkipForward,
  IconDotsVertical,
  IconNotes,
  IconPencil,
  IconRepeat,
} from '@tabler/icons-react';
import type { TaskInstance } from '@/types/task';
import { TASK_TYPE_LABELS, TASK_TYPE_COLORS } from '@/types/task';

interface TaskInstanceCardProps {
  instance: TaskInstance;
  isFixed: boolean;
  scheduleLabel: string | null;
  compactScheduleLabel: string | null;
  onComplete: (instance: TaskInstance) => void;
  onSnooze: (instance: TaskInstance) => void;
  onSkip: (instance: TaskInstance) => void;
  onEditTask: (instance: TaskInstance) => void;
}

function isOverdue(dueAt: string): boolean {
  const due = new Date(dueAt);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
}

function isDueToday(dueAt: string): boolean {
  const due = new Date(dueAt);
  const today = new Date();
  return due.toDateString() === today.toDateString();
}

function formatDueDateShort(dueAt: string): string {
  const due = new Date(dueAt);
  const today = new Date();
  if (due.toDateString() === today.toDateString()) {
    return 'Today';
  }
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };
  if (due.getFullYear() !== today.getFullYear()) {
    options.year = 'numeric';
  }
  return due.toLocaleDateString(undefined, options);
}

export function TaskInstanceCard({
  instance,
  isFixed,
  scheduleLabel,
  compactScheduleLabel,
  onComplete,
  onSnooze,
  onSkip,
  onEditTask,
}: TaskInstanceCardProps) {
  const overdue = isOverdue(instance.due_at);
  const dueToday = isDueToday(instance.due_at);
  const taskType = instance.task_type || 'cleaning';
  const hasInstructions = !!instance.template_data?.instructions;
  const isSnoozed = instance.status === 'snoozed' && !!instance.snoozed_until;

  // Determine the recurring indicator text
  const hasLimit = instance.recurrence_limit != null && instance.completion_count != null;
  const recurringText = hasLimit
    ? `${instance.completion_count} of ${instance.recurrence_limit}`
    : compactScheduleLabel;
  const recurringTooltip = hasLimit
    ? `${instance.completion_count} of ${instance.recurrence_limit} completions${scheduleLabel ? ` · ${scheduleLabel}` : ''}`
    : scheduleLabel;

  const handleRowClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[role="menuitem"]')) {
      return;
    }
    onComplete(instance);
  };

  return (
    <Box
      style={{
        cursor: 'pointer',
        padding: '8px 4px',
        borderBottom: '1px solid var(--mantine-color-gray-2)',
        transition: 'background-color 150ms ease',
      }}
      onClick={handleRowClick}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--mantine-color-gray-0)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
      }}
    >
      <Group gap="sm" wrap="nowrap">
        {/* Complete checkbox */}
        <ActionIcon
          variant="subtle"
          color={overdue ? 'red' : 'gray'}
          size="sm"
          aria-label="Complete"
          onClick={(e) => {
            e.stopPropagation();
            onComplete(instance);
          }}
        >
          <IconCircle size={18} strokeWidth={1.5} />
        </ActionIcon>

        {/* Task name */}
        <Text
          fw={500}
          size="sm"
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {instance.name}
          {hasInstructions && (
            <Tooltip label="Has instructions">
              <IconNotes
                size={14}
                color="gray"
                data-testid="notes-icon"
                style={{ marginLeft: 4, verticalAlign: 'text-bottom' }}
              />
            </Tooltip>
          )}
        </Text>

        {/* Category badge — hidden on mobile to save space */}
        <Badge
          color={TASK_TYPE_COLORS[taskType]}
          variant="light"
          size="sm"
          style={{ flexShrink: 0 }}
          visibleFrom="sm"
        >
          {TASK_TYPE_LABELS[taskType]}
        </Badge>

        {/* Recurring indicator */}
        {recurringText && (
          <Tooltip label={recurringTooltip || recurringText}>
            <Group
              gap={4}
              wrap="nowrap"
              style={{ flexShrink: 0 }}
              data-testid="recurring-indicator"
            >
              <IconRepeat size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                {recurringText}
              </Text>
            </Group>
          </Tooltip>
        )}

        {/* Due date */}
        <Tooltip label={overdue ? 'Overdue' : dueToday ? 'Due Today' : undefined} disabled={!overdue && !dueToday}>
          <Text
            size="sm"
            c={overdue ? 'red' : dueToday ? 'orange' : 'dimmed'}
            fw={overdue ? 600 : undefined}
            style={{ flexShrink: 0 }}
            data-testid="due-date"
          >
            {formatDueDateShort(instance.due_at)}
          </Text>
        </Tooltip>

        {/* Snooze icon button */}
        <Tooltip label={isSnoozed ? `Snoozed until ${formatDueDateShort(instance.snoozed_until!)}` : 'Snooze'}>
          <ActionIcon
            variant="subtle"
            color={isSnoozed ? 'blue' : 'gray'}
            size="sm"
            aria-label="Snooze"
            onClick={(e) => {
              e.stopPropagation();
              onSnooze(instance);
            }}
          >
            <IconClock size={16} />
          </ActionIcon>
        </Tooltip>

        {/* Overflow menu */}
        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" size="sm" onClick={(e) => e.stopPropagation()}>
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconPencil size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onEditTask(instance);
              }}
            >
              Edit Task
            </Menu.Item>
            {isFixed && (
              <Menu.Item
                leftSection={<IconPlayerSkipForward size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onSkip(instance);
                }}
              >
                Skip
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Box>
  );
}
